import {
  Button,
  ErrorMessage,
  formatNumber,
  modalHandler,
  success,
  useCustomForm,
  useState,
  validate,
  yup,
} from "@";
import {
  ArchiveBoxIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  PhotoIcon,
  TrashIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { useAddFilesMutation } from "../api/mutations/use-add-files.ts";
import { VerifySearchValues } from "../types.ts";
import React from "react";
import { Progress, Upload } from "@/shared/components";
import PreviousZipFilesPanel from "./PreviousZipFilesPanel.tsx";

// get file size in MB or return empty string if no files
const getFileSize = (files: File[]): string => {
  if (files.length === 0) return "";
  return (
    (
      Array.from(files).reduce((acc, file) => acc + file.size, 0) /
      1024 /
      1024
    ).toFixed(2) + " MB"
  );
};

interface BulkUploadModalProps {
  ids: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
  onSuccess: () => void;
  totalItems: number;
}

type FormValues = {
  photos: File[];
  videos: File[];
  zipFile: File[];
  selectedZipKey: string | null;
};

const schema = yup
  .object({
    photos: validate("nullable-photos"),
    videos: validate("nullable-videos"),
    zipFile: validate("nullable-zip"),
    selectedZipKey: yup.string().nullable(),
  })
  .test("at-least-one-file", "رجاء رفع ملف واحد على الاقل", (values) => {
    const { photos, videos, zipFile, selectedZipKey } = values;
    return (
      (photos?.length || 0) +
        (videos?.length || 0) +
        (zipFile?.length || 0) +
        (selectedZipKey ? 1 : 0) >
      0
    );
  });

const defaultValues: FormValues = {
  photos: [],
  videos: [],
  zipFile: [],
  selectedZipKey: null,
};

const MODAL_ID = "bulk-upload-modal";

const getFieldError = (fieldErrors: any): string | undefined => {
  if (!fieldErrors) return undefined;
  // Array-level error (e.g. .max())
  if (fieldErrors.message) return fieldErrors.message;
  // Per-item errors from .of() — return first one found
  if (Array.isArray(fieldErrors)) {
    const first = fieldErrors.find((e: any) => e?.message);
    return first?.message;
  }
  return undefined;
};

const BulkUploadModal = ({
  ids,
  selectingAll,
  searchValues,
  onSuccess,
  totalItems,
}: BulkUploadModalProps) => {
  const addFilesMutation = useAddFilesMutation();

  const { setValue, handleSubmit, errors, reset, watch } = useCustomForm(
    defaultValues,
    schema,
  );

  const [mode, setMode] = useState<"media" | "zip">("media");
  const [zipTab, setZipTab] = useState<"upload" | "previous">("upload");

  const schemaError = (errors as any)?.[""]?.message as string | undefined;

  const photos = watch("photos") as File[] | undefined;
  const videos = watch("videos") as File[] | undefined;
  const zipFile = watch("zipFile") as File[] | undefined;
  const selectedZipKey = watch("selectedZipKey") as string | null | undefined;
  const zipFileSize = getFileSize(zipFile || []);
  const photosSize = getFileSize(photos || []);
  const videosSize = getFileSize(videos || []);

  const switchMode = (next: "media" | "zip") => {
    if (next === mode) return;
    setMode(next);
    if (next === "media") {
      setValue("zipFile", [] as any);
      setValue("selectedZipKey", null as any);
    } else {
      setValue("photos", [] as any);
      setValue("videos", [] as any);
    }
  };

  const switchZipTab = (next: "upload" | "previous") => {
    if (next === zipTab) return;
    setZipTab(next);
    if (next === "previous") {
      setValue("zipFile", [] as any);
    } else {
      setValue("selectedZipKey", null as any);
    }
  };

  const createUploadHandler =
    (field: keyof FormValues, replace = false) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(e.target.files || []);
      if (replace) {
        setValue(field, newFiles as any);
      } else {
        const current = (watch(field) as File[]) || [];
        setValue(field, [...current, ...newFiles] as any);
      }
    };

  const onSubmit = async (data: FormValues) => {
    try {
      await addFilesMutation.mutateAsync({
        verificationStagesIds: ids,
        selectingAll: selectingAll,
        searchValues: searchValues,
        photos: data.photos,
        videos: data.videos,
        zipFile: data.zipFile,
        zipKey: data.selectedZipKey ?? undefined,
      });
      modalHandler(MODAL_ID);
      await success("تم الرفع بنجاح");
      onSuccess();
      reset();
      setMode("media");
      setZipTab("upload");
    } catch (error) {
      console.error("onSubmit:error", error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-7 p-2 md:p-5">
      {ids.length === 0 && (
        <ErrorMessage className="mb-5" value="رجاء تحديد طلب واحد على الاقل" />
      )}

      {schemaError && <ErrorMessage className="mb-3" value={schemaError} />}

      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          عدد الطلبات المحددة:
          {formatNumber(selectingAll ? totalItems : ids.length)}
        </p>
        <p className="text-sm text-gray-900">
          اختر إحدى الطريقتين لرفع التوثيق: صور وفيديوهات، أو ملف مضغوط واحد.
        </p>
      </div>

      {/* Top-level mode tabs: media vs zip */}
      <div
        role="tablist"
        className="grid grid-cols-2 gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-700/60"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "media"}
          onClick={() => switchMode("media")}
          disabled={ids.length === 0}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
            mode === "media"
              ? "bg-white text-purple-800 shadow-sm dark:bg-gray-800 dark:text-purple-300"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          }`}
        >
          <PhotoIcon className="h-4 w-4" />
          صور وفيديوهات
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "zip"}
          onClick={() => switchMode("zip")}
          disabled={ids.length === 0}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
            mode === "zip"
              ? "bg-white text-purple-800 shadow-sm dark:bg-gray-800 dark:text-purple-300"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
          }`}
        >
          <ArchiveBoxIcon className="h-4 w-4" />
          ملف مضغوط
        </button>
      </div>

      {mode === "media" ? (
      <div className="grid gap-5 md:grid-cols-2">
        {/* photos */}
        <section>
          <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            الصور
          </h3>
          <Upload
            multiple
            accept="image/*"
            upload={createUploadHandler("photos")}
            title="اضافة الصور"
            text={
              photos?.length
                ? `${photos.length} صورة — اضغط للإضافة`
                : undefined
            }
            disabled={ids.length === 0}
          >
            <PhotoIcon className="h-6 w-6 text-gray-300 dark:text-gray-400" />
            {photosSize && photosSize !== "" && (
              <p className="mx-1 text-xs text-gray-400">{photosSize}</p>
            )}
          </Upload>
          {(getFieldError(errors.photos) || !!photos?.length) && (
            <div className="mt-2 flex items-center justify-between">
              {getFieldError(errors.photos) ? (
                <ErrorMessage value={getFieldError(errors.photos) as string} />
              ) : (
                <span />
              )}
              {!!photos?.length && (
                <button
                  type="button"
                  onClick={() => setValue("photos", [] as any)}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <TrashIcon className="h-4 w-4" />
                  مسح الصور
                </button>
              )}
            </div>
          )}
        </section>

        {/* Videos */}
        <section>
          <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            الفيديوهات
          </h3>
          <Upload
            multiple
            accept="video/*"
            upload={createUploadHandler("videos")}
            title="اضافة الفيديوهات"
            text={
              videos?.length
                ? `${videos.length} فيديو — اضغط للإضافة`
                : undefined
            }
            disabled={ids.length === 0}
          >
            <VideoCameraIcon className="h-6 w-6 text-gray-300 dark:text-gray-400" />
            {videosSize && videosSize !== "" && (
              <p className="mx-1 text-xs text-gray-400">{videosSize}</p>
            )}
          </Upload>
          {(getFieldError(errors.videos) || !!videos?.length) && (
            <div className="mt-2 flex items-center justify-between">
              {getFieldError(errors.videos) ? (
                <ErrorMessage value={getFieldError(errors.videos) as string} />
              ) : (
                <span />
              )}
              {!!videos?.length && (
                <button
                  type="button"
                  onClick={() => setValue("videos", [] as any)}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <TrashIcon className="h-4 w-4" />
                  مسح الفيديوهات
                </button>
              )}
            </div>
          )}
        </section>
      </div>
      ) : (
        <section className="space-y-3">
          <div
            role="tablist"
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 p-1 dark:bg-gray-700/60"
          >
            <button
              type="button"
              role="tab"
              aria-selected={zipTab === "upload"}
              onClick={() => switchZipTab("upload")}
              disabled={ids.length === 0}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                zipTab === "upload"
                  ? "bg-white text-purple-800 shadow-sm dark:bg-gray-800 dark:text-purple-300"
                  : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
              }`}
            >
              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
              رفع جديد
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={zipTab === "previous"}
              onClick={() => switchZipTab("previous")}
              disabled={ids.length === 0}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                zipTab === "previous"
                  ? "bg-white text-purple-800 shadow-sm dark:bg-gray-800 dark:text-purple-300"
                  : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
              }`}
            >
              <ClockIcon className="h-3.5 w-3.5" />
              اختيار من السابق
            </button>
          </div>

          {zipTab === "upload" ? (
            <>
              <Upload
                accept=".zip"
                upload={createUploadHandler("zipFile", true)}
                title="اضافة ملف مضغوط"
                text={zipFile?.length ? `${zipFile[0]?.name}` : undefined}
                disabled={ids.length === 0}
              >
                <ArchiveBoxIcon className="h-6 w-6 text-gray-300 dark:text-gray-400" />

                {zipFileSize && zipFileSize !== "" && (
                  <p className="mx-1 text-xs text-gray-400">{zipFileSize}</p>
                )}
              </Upload>
              <div className="mt-2 flex items-center justify-between">
                {getFieldError(errors.zipFile) ? (
                  <ErrorMessage
                    className="mt-2"
                    value={getFieldError(errors.zipFile) as string}
                  />
                ) : (
                  <span />
                )}
                {!!zipFile?.length && (
                  <button
                    type="button"
                    onClick={() => setValue("zipFile", [] as any)}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                    مسح الملف
                  </button>
                )}
              </div>
            </>
          ) : (
            <PreviousZipFilesPanel
              selectedKey={selectedZipKey ?? null}
              onSelect={(key) => setValue("selectedZipKey", key as any)}
              enabled={zipTab === "previous" && ids.length > 0}
            />
          )}
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        {ids.length > 0 && (
          <Button
            type="submit"
            title="رفع الملفات"
            isLoading={addFilesMutation.isPending}
            className="flex-1 py-3"
          >
            <ArrowUpTrayIcon className="h-5 w-5" />
          </Button>
        )}

        <Progress show={addFilesMutation.isPending} />
      </div>
    </form>
  );
};

export default BulkUploadModal;
